import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "@emotion/styled";
import {
  ContentType,
  isRulePane,
} from "@financial-advisory/data-access/api-types";
import { ProcessedClause } from "@financial-advisory/data-access/api-types";
import {
  selectActiveClause,
  selectClauseByHighlightId,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import { setActiveClauseById } from "@financial-advisory/data-access/financial-advisory-api-service";
import {
  Scaled,
  type ScaledPosition,
} from "@financial-advisory/ui/components/pdf-viewer";
import {
  FloatingPortal,
  autoPlacement,
  offset,
  shift,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { useFloating } from "@floating-ui/react";
import { cn } from "@unlikelyai-magic/utils";
import { ContentTypeExplanation } from "../HighlightColorKeys";
import { ContentTypeToHighlightKeyMap } from "../HighlightColorKeys";

const Parts = styled.div`
  opacity: 1;
`;

const Container = styled.div`
  position: absolute;
`;

export const ContentTypeColors: Record<ContentType, string> = {
  RULE: "bg-yellow-100",
  DEFINITION: "bg-green-100",
  GUIDANCE: "bg-violet-100",
  EXAMPLE: "bg-blue-100",
  CONTEXT: "bg-pink-100",
  NOT_APPLICABLE: "bg-gray-100 node-striped-background",
};

const getPrimaryContentType = (contentTypes: ContentType[]): ContentType => {
  if (contentTypes.includes("RULE")) {
    return "RULE";
  } else if (contentTypes.includes("DEFINITION")) {
    return "DEFINITION";
  } else if (contentTypes.includes("EXAMPLE")) {
    return "EXAMPLE";
  } else if (contentTypes.includes("GUIDANCE")) {
    return "GUIDANCE";
  } else {
    // Find any content type that is not NOT_APPLICABLE
    const nonNotApplicable = contentTypes.find(
      (contentType) => contentType !== "NOT_APPLICABLE"
    );
    return nonNotApplicable ?? "NOT_APPLICABLE";
  }
};

interface Props {
  highlightId: string;
  position: ScaledPosition;
  contentType?: ContentType;
}

const LineHighlight = ({
  rect,
  clause,
  isActive,
  directContentType,
}: {
  rect: Scaled;
  clause?: ProcessedClause;
  isActive: boolean;
  directContentType?: ContentType;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    strategy: "absolute",
    middleware: [
      autoPlacement({
        allowedPlacements: ["top-start", "bottom-start"],
      }),
      offset(8),
      shift({
        padding: 8,
      }),
    ],
  });
  const hover = useHover(context, {
    delay: {
      open: 500,
      close: 50,
    },
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);
  const contentType: ContentType = getPrimaryContentType(
    (clause?.panes?.map((pane) => pane.paneType) as ContentType[]) ?? [
      "NOT_APPLICABLE",
    ]
  );

  const applicableContentType: ContentType = useMemo(() => {
    // Use directly provided contentType if available (e.g., from guardrail edit data)
    if (directContentType) return directContentType;

    if (!clause?.panes?.length) return "NOT_APPLICABLE";

    // A rule is applicable if its evaluated trigger is true or dk
    if (
      clause.panes.some(
        (pane) =>
          pane.paneType === "RULE" &&
          isRulePane(pane) &&
          pane.rule.evaluatedTrigger.evaluationResult !== "False"
      )
    ) {
      return "RULE";
    } else {
      return clause.panes.some((pane) => pane.paneType === "DEFINITION")
        ? "DEFINITION"
        : "NOT_APPLICABLE";
    }
  }, [directContentType, clause]);

  return (
    <>
      <div
        style={rect}
        className={cn(
          "cursor-pointer absolute transition-colors duration-300",
          ContentTypeColors[applicableContentType],
          clause && isActive && "ring-2 ring-blue-500",
          clause && !isActive && "ring-0"
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
      />
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="max-w-md flex flex-col gap-2 bg-white p-2 rounded-lg p-4 border border-gray-200 shadow-xl z-50"
          >
            <div className="flex flex-row gap-2 flex-wrap">
              {Array.from(
                new Set(
                  clause?.panes
                    ?.map((pane) => pane.paneType as ContentType)
                    .filter(
                      (type) => ContentTypeToHighlightKeyMap[type] !== undefined
                    )
                )
              )
                .sort((a, b) => {
                  if (a === "RULE") return -1;
                  if (b === "RULE") return 1;
                  if (a === "DEFINITION") return -1;
                  if (b === "DEFINITION") return 1;
                  return 0;
                })
                .map((type) => (
                  <div
                    key={type}
                    className={cn(
                      "px-2 py-1 w-fit rounded-xl rounded capitalize text-center font-semibold",
                      ContentTypeColors[type]
                    )}
                  >
                    {ContentTypeToHighlightKeyMap[type].replace("_", " ")}
                  </div>
                ))}
            </div>
            <div className="text-sm font-semibold text-gray-700">
              {clause?.panes?.[0]?.explanation
                ? clause.panes[0].explanation
                : ContentTypeExplanation[contentType]}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export const TextHighlight = ({ highlightId, position, contentType }: Props) => {
  const dispatch = useDispatch();
  // Only do Redux lookup if contentType is not directly provided
  const clause: ProcessedClause | undefined = useSelector(
    selectClauseByHighlightId(highlightId)
  );
  const activeClause: ProcessedClause | undefined =
    useSelector(selectActiveClause);
  const { rects } = position;
  const isActiveClause = Boolean(
    clause && activeClause && clause.id === activeClause.id
  );

  return (
    <Container>
      <Parts
        id={`highlight-${highlightId}`}
        onClick={
          clause
            ? () => {
                dispatch(setActiveClauseById(clause.id));
              }
            : undefined
        }
      >
        {rects.map((rect, index) => (
          <LineHighlight
            key={index}
            clause={clause}
            rect={rect}
            isActive={isActiveClause}
            directContentType={contentType}
          />
        ))}
      </Parts>
    </Container>
  );
};
